"use client";

import { c as _c } from "react/compiler-runtime";
import { getScaledImageUri, ImageScale } from "@/helpers/image.helpers";
import { getTimeAgoShort, numberWithCommas } from "@/helpers/Helpers";
import Link from "next/link";
import Drop, { DropInteractionParams, DropLocation } from "@/components/waves/drops/Drop";
import { ActiveDropState } from "@/types/dropInteractionTypes";
import { useRouter } from "next/navigation";
import { ApiDrop } from "@/generated/models/ApiDrop";
import { DropSize, ExtendedDrop } from "@/helpers/waves/drop.helpers";
import NotificationsFollowBtn from "../NotificationsFollowBtn";
import { UserFollowBtnSize } from "@/components/user/utils/UserFollowBtn";
import { useEmoji } from "@/contexts/EmojiContext";
import type { INotificationDropVoted, INotificationDropReacted } from "@/types/feed.types";
import UserProfileTooltipWrapper from "@/components/utils/tooltip/UserProfileTooltipWrapper";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { getWaveRoute } from "@/helpers/navigation.helpers";
export const getNotificationVoteColor = (vote: number) => {
  if (vote > 0) return "tw-text-green";
  if (vote < 0) return "tw-text-red";
  return "tw-text-iron-500";
};
type NotificationUnion = INotificationDropVoted | INotificationDropReacted;
interface Props {
  readonly notification: NotificationUnion;
  readonly activeDrop: ActiveDropState | null;
  readonly onReply: (param: DropInteractionParams) => void;
  readonly onQuote: (param: DropInteractionParams) => void;
  readonly onDropContentClick?: (drop: ExtendedDrop) => void;
}
export default function NotificationDropReacted(t0) {
  const $ = _c(81);
  const {
    notification,
    activeDrop,
    onReply,
    onQuote,
    onDropContentClick
  } = t0;
  const router = useRouter();
  const {
    findCustomEmoji,
    findNativeEmoji
  } = useEmoji();
  const {
    isApp
  } = useDeviceInfo();
  const isVoted = (notification as INotificationDropVoted).additional_context.vote !== undefined;
  const isReacted = (notification as INotificationDropReacted).additional_context.reaction !== undefined;
  let actionElement;
  if (isVoted) {
    const voteValue = (notification as INotificationDropVoted).additional_context.vote;
    let t1;
    if ($[0] !== notification.created_at) {
      t1 = getTimeAgoShort(notification.created_at);
      $[0] = notification.created_at;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    const timeAgo = t1;
    const t2 = notification.related_identity.handle ?? "";
    const t3 = `/${notification.related_identity.handle}`;
    let t4;
    if ($[2] !== notification.related_identity.handle || $[3] !== t3) {
      t4 = <Link href={t3} className="tw-no-underline tw-font-semibold tw-text-sm tw-text-iron-50">{notification.related_identity.handle}</Link>;
      $[2] = notification.related_identity.handle;
      $[3] = t3;
      $[4] = t4;
    } else {
      t4 = $[4];
    }
    let t5;
    if ($[5] !== t2 || $[6] !== t4) {
      t5 = <UserProfileTooltipWrapper user={t2}>{t4}</UserProfileTooltipWrapper>;
      $[5] = t2;
      $[6] = t4;
      $[7] = t5;
    } else {
      t5 = $[7];
    }
    let t6;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <span className="tw-text-iron-400 tw-font-normal tw-text-sm">rated</span>;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    let t7;
    if ($[9] !== voteValue) {
      t7 = getNotificationVoteColor(voteValue);
      $[9] = voteValue;
      $[10] = t7;
    } else {
      t7 = $[10];
    }
    const t8 = `${t7} tw-pl-1 tw-font-medium tw-text-sm`;
    const t9 = voteValue > 0 && "+";
    let t10;
    if ($[11] !== voteValue) {
      t10 = numberWithCommas(voteValue);
      $[11] = voteValue;
      $[12] = t10;
    } else {
      t10 = $[12];
    }
    let t11;
    if ($[13] !== t10 || $[14] !== t8 || $[15] !== t9) {
      t11 = <span className={t8}>{t9}{t10}</span>;
      $[13] = t10;
      $[14] = t8;
      $[15] = t9;
      $[16] = t11;
    } else {
      t11 = $[16];
    }
    let t12;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <span className="tw-font-bold tw-mr-1 tw-text-xs tw-text-iron-400">•</span>;
      $[17] = t12;
    } else {
      t12 = $[17];
    }
    let t13;
    if ($[18] !== timeAgo) {
      t13 = <span className="tw-text-sm tw-text-iron-300 tw-font-normal tw-whitespace-nowrap">{t12}{timeAgo}</span>;
      $[18] = timeAgo;
      $[19] = t13;
    } else {
      t13 = $[19];
    }
    let t14;
    if ($[20] !== t11 || $[21] !== t13 || $[22] !== t5) {
      t14 = <>{t5}{t6}{t11}{t13}</>;
      $[20] = t11;
      $[21] = t13;
      $[22] = t5;
      $[23] = t14;
    } else {
      t14 = $[23];
    }
    actionElement = t14;
  } else {
    if (isReacted) {
      const t1 = notification as INotificationDropReacted;
      let emojiNode;
      if ($[24] !== findCustomEmoji || $[25] !== findNativeEmoji || $[26] !== t1.additional_context.reaction) {
        const rawId = t1.additional_context.reaction.replaceAll(":", "");
        emojiNode = null;
        const custom = findCustomEmoji(rawId);
        if (custom) {
          emojiNode = <img src={custom.skins[0].src} alt={rawId} className="tw-max-w-5 tw-max-h-5 tw-object-contain" />;
        } else {
          const native = findNativeEmoji(rawId);
          if (native) {
            let t2;
            if ($[28] !== native.skins[0].native) {
              t2 = <span className="tw-text-[1.2rem] tw-flex tw-items-center tw-justify-center">{native.skins[0].native}</span>;
              $[28] = native.skins[0].native;
              $[29] = t2;
            } else {
              t2 = $[29];
            }
            emojiNode = t2;
          }
        }
        $[24] = findCustomEmoji;
        $[25] = findNativeEmoji;
        $[26] = t1.additional_context.reaction;
        $[27] = emojiNode;
      } else {
        emojiNode = $[27];
      }
      if (!emojiNode) {
        return null;
      }
      let t2;
      if ($[30] !== notification.created_at) {
        t2 = getTimeAgoShort(notification.created_at);
        $[30] = notification.created_at;
        $[31] = t2;
      } else {
        t2 = $[31];
      }
      const timeAgo_0 = t2;
      const t3 = notification.related_identity.handle ?? "";
      const t4 = `/${notification.related_identity.handle}`;
      let t5;
      if ($[32] !== notification.related_identity.handle || $[33] !== t4) {
        t5 = <Link href={t4} className="tw-no-underline tw-font-semibold tw-text-sm tw-text-iron-50">{notification.related_identity.handle}</Link>;
        $[32] = notification.related_identity.handle;
        $[33] = t4;
        $[34] = t5;
      } else {
        t5 = $[34];
      }
      let t6;
      if ($[35] !== t3 || $[36] !== t5) {
        t6 = <UserProfileTooltipWrapper user={t3}>{t5}</UserProfileTooltipWrapper>;
        $[35] = t3;
        $[36] = t5;
        $[37] = t6;
      } else {
        t6 = $[37];
      }
      let t7;
      if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
        t7 = <span className="tw-text-iron-400 tw-font-normal tw-text-sm">reacted</span>;
        $[38] = t7;
      } else {
        t7 = $[38];
      }
      let t8;
      if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
        t8 = <span className="tw-font-bold tw-mr-1 tw-text-xs tw-text-iron-400">•</span>;
        $[39] = t8;
      } else {
        t8 = $[39];
      }
      let t9;
      if ($[40] !== timeAgo_0) {
        t9 = <span className="tw-text-sm tw-text-iron-300 tw-font-normal tw-whitespace-nowrap">{t8}{timeAgo_0}</span>;
        $[40] = timeAgo_0;
        $[41] = t9;
      } else {
        t9 = $[41];
      }
      let t10;
      if ($[42] !== emojiNode || $[43] !== t6 || $[44] !== t9) {
        t10 = <>{t6}{t7}{emojiNode}{t9}</>;
        $[42] = emojiNode;
        $[43] = t6;
        $[44] = t9;
        $[45] = t10;
      } else {
        t10 = $[45];
      }
      actionElement = t10;
    } else {
      return null;
    }
  }
  const baseWave = notification.related_drops[0].wave as any;
  const baseIsDm = baseWave?.chat?.scope?.group?.is_direct_message ?? false;
  let t1;
  if ($[46] !== baseIsDm || $[47] !== baseWave.id || $[48] !== isApp || $[49] !== router) {
    t1 = serialNo => {
      router.push(getWaveRoute({
        waveId: baseWave.id,
        serialNo,
        isDirectMessage: baseIsDm,
        isApp
      }));
    };
    $[46] = baseIsDm;
    $[47] = baseWave.id;
    $[48] = isApp;
    $[49] = router;
    $[50] = t1;
  } else {
    t1 = $[50];
  }
  const onReplyClick = t1;
  let t2;
  if ($[51] !== baseIsDm || $[52] !== isApp || $[53] !== router) {
    t2 = quote => {
      const quoteWave = quote.wave as any;
      const isDirectMessage = quoteWave?.chat?.scope?.group?.is_direct_message ?? baseIsDm;
      router.push(getWaveRoute({
        waveId: quote.wave.id,
        serialNo: quote.serial_no,
        isDirectMessage,
        isApp
      }));
    };
    $[51] = baseIsDm;
    $[52] = isApp;
    $[53] = router;
    $[54] = t2;
  } else {
    t2 = $[54];
  }
  const onQuoteClick = t2;
  let t3;
  if ($[55] !== notification.related_identity.handle || $[56] !== notification.related_identity.pfp) {
    t3 = <div className="tw-h-7 tw-w-7">{notification.related_identity.pfp ? <img src={getScaledImageUri(notification.related_identity.pfp, ImageScale.W_AUTO_H_50)} alt={notification.related_identity.handle ?? ""} className="tw-flex-shrink-0 tw-object-contain tw-h-full tw-w-full tw-rounded-md tw-bg-iron-800 tw-ring-1 tw-ring-iron-700" /> : <div className="tw-flex-shrink-0 tw-object-contain tw-h-full tw-w-full tw-rounded-md tw-bg-iron-800 tw-ring-1 tw-ring-iron-700" />}</div>;
    $[55] = notification.related_identity.handle;
    $[56] = notification.related_identity.pfp;
    $[57] = t3;
  } else {
    t3 = $[57];
  }
  let t4;
  if ($[58] !== actionElement) {
    t4 = <span className="tw-inline-flex tw-flex-wrap tw-gap-x-1 tw-items-center">{actionElement}</span>;
    $[58] = actionElement;
    $[59] = t4;
  } else {
    t4 = $[59];
  }
  let t5;
  if ($[60] !== t3 || $[61] !== t4) {
    t5 = <div className="tw-flex-1 tw-flex tw-gap-x-2 tw-items-center">{t3}{t4}</div>;
    $[60] = t3;
    $[61] = t4;
    $[62] = t5;
  } else {
    t5 = $[62];
  }
  let t6;
  if ($[63] !== notification.related_identity) {
    t6 = <NotificationsFollowBtn profile={notification.related_identity} size={UserFollowBtnSize.SMALL} />;
    $[63] = notification.related_identity;
    $[64] = t6;
  } else {
    t6 = $[64];
  }
  let t7;
  if ($[65] !== t5 || $[66] !== t6) {
    t7 = <div className="tw-flex tw-justify-between tw-gap-x-4 tw-gap-y-1">{t5}{t6}</div>;
    $[65] = t5;
    $[66] = t6;
    $[67] = t7;
  } else {
    t7 = $[67];
  }
  let t8;
  if ($[68] !== notification.related_drops[0]) {
    t8 = {
      type: DropSize.FULL,
      ...notification.related_drops[0],
      stableKey: "",
      stableHash: ""
    };
    $[68] = notification.related_drops[0];
    $[69] = t8;
  } else {
    t8 = $[69];
  }
  let t9;
  if ($[70] !== activeDrop || $[71] !== onDropContentClick || $[72] !== onQuote || $[73] !== onQuoteClick || $[74] !== onReply || $[75] !== onReplyClick || $[76] !== t8) {
    t9 = <Drop drop={t8} previousDrop={null} nextDrop={null} showWaveInfo={true} showReplyAndQuote={true} activeDrop={activeDrop} location={DropLocation.MY_STREAM} dropViewDropId={null} onReply={onReply} onQuote={onQuote} onReplyClick={onReplyClick} onQuoteClick={onQuoteClick} onDropContentClick={onDropContentClick} />;
    $[70] = activeDrop;
    $[71] = onDropContentClick;
    $[72] = onQuote;
    $[73] = onQuoteClick;
    $[74] = onReply;
    $[75] = onReplyClick;
    $[76] = t8;
    $[77] = t9;
  } else {
    t9 = $[77];
  }
  let t10;
  if ($[78] !== t7 || $[79] !== t9) {
    t10 = <div className="tw-flex tw-gap-x-3 tw-w-full"><div className="tw-space-y-2 tw-w-full">{t7}{t9}</div></div>;
    $[78] = t7;
    $[79] = t9;
    $[80] = t10;
  } else {
    t10 = $[80];
  }
  return t10;
}